import { useState } from 'react';
import kit from './kit.module.css';
import styles from './ConfidenceDisclosureDemo.module.css';

type Scope = 'one' | 'future' | 'all';
type Stage = 'ask' | 'confirmAll' | 'done';

const SCOPE_LABEL: Record<Scope, string> = {
  one: 'Just this charge',
  future: 'This and future charges',
  all: 'All charges, including August',
};

const DEFAULT_FEEDBACK =
  'The agent is unsure how far the change should reach, so it asks first. Pick a scope.';

interface RecordRow {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

function recordsFor(scope: Scope | null): RecordRow[] {
  const one = scope !== null;
  const future = scope === 'future' || scope === 'all';
  const all = scope === 'all';
  return [
    { label: 'Sep 14 charge - $48.20', before: 'Shopping', after: 'Groceries', changed: one },
    { label: '11 earlier charges', before: 'Shopping', after: 'Groceries', changed: all },
    { label: 'August report (closed)', before: 'Closed, sent', after: 'Reopened, totals +$212', changed: all },
    { label: 'New Brightway charges', before: 'No rule', after: 'Rule: Groceries', changed: future },
  ];
}

function confirmationFor(scope: Scope) {
  if (scope === 'one') {
    return 'Done. Changed: the Sep 14 charge. Not changed: the 11 earlier charges, your closed August report, and future Brightway charges.';
  }
  if (scope === 'future') {
    return 'Done. Changed: the Sep 14 charge, plus a rule for new Brightway charges. Not changed: the 11 earlier charges and your closed August report.';
  }
  return 'Done. Changed: all 12 Brightway charges, a rule for new ones, and your August report, which is now reopened with Groceries up $212. Nothing else was touched.';
}

export function ConfidenceDisclosureDemo() {
  const [stage, setStage] = useState<Stage>('ask');
  const [scope, setScope] = useState<Scope | null>(null);
  const [showRule, setShowRule] = useState(false);
  const [feedback, setFeedback] = useState(DEFAULT_FEEDBACK);

  const choose = (next: Scope) => {
    if (next === 'all') {
      setStage('confirmAll');
      setFeedback('This one affects a report you already shared, so the agent flags the stakes before acting.');
      return;
    }
    apply(next);
  };

  const apply = (next: Scope) => {
    setScope(next);
    setStage('done');
    setShowRule(false);
    setFeedback(
      next === 'all'
        ? 'Applied everywhere. The confirmation names the closed report so nothing changes silently.'
        : 'Applied. The reply lists what changed and what was left alone - check the records panel.'
    );
  };

  const undo = () => {
    setScope(null);
    setStage('ask');
    setShowRule(false);
    setFeedback('Undone. Every record is back to how it was. Try a different scope.');
  };

  const records = recordsFor(stage === 'done' ? scope : null);

  return (
    <div className={kit.root}>
      <div className={kit.split}>
        <div className={kit.stack}>
          <div className={kit.bubbleUser}>Brightway Market should be Groceries, not Shopping.</div>

          <div className={kit.bubbleAgent}>
            Got it. I'm not sure how far this should go - Brightway also sells household goods, and
            you have 11 earlier charges there, 4 in August, which you've already closed.
          </div>

          {stage === 'ask' && (
            <div className={kit.stack}>
              {(Object.keys(SCOPE_LABEL) as Scope[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${kit.btn} ${styles.option}`}
                  onClick={() => choose(s)}
                >
                  {SCOPE_LABEL[s]}
                </button>
              ))}
            </div>
          )}

          {stage === 'confirmAll' && (
            <>
              <div className={kit.bubbleUser}>{SCOPE_LABEL.all}.</div>
              <div className={kit.proposal}>
                <div className={kit.proposalTop}>
                  <span>Check before changing</span>
                  <span>Affects a shared report</span>
                </div>
                <h3 className={kit.proposalTitle}>Reopen your August report?</h3>
                <dl className={kit.fields}>
                  <dt>Report</dt>
                  <dd>Closed and sent to Jonah Reyes on Sep 2</dd>
                  <dt>Effect</dt>
                  <dd>Groceries +$212, Shopping -$212 for August</dd>
                  <dt>Charges</dt>
                  <dd>All 12 Brightway charges, plus new ones</dd>
                </dl>
                <div className={`${kit.row} ${styles.actions}`}>
                  <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={() => apply('all')}>
                    Yes, reopen August
                  </button>
                  <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => apply('future')}>
                    Leave August, apply to future
                  </button>
                  <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={undo}>
                    Go back
                  </button>
                </div>
              </div>
            </>
          )}

          {stage === 'done' && scope && (
            <>
              <div className={kit.bubbleUser}>{SCOPE_LABEL[scope]}.</div>
              <div className={kit.bubbleAgent}>{confirmationFor(scope)}</div>
              {showRule && scope !== 'one' && (
                <div className={kit.proposal}>
                  <div className={kit.proposalTop}>
                    <span>Rule</span>
                    <span>Active</span>
                  </div>
                  <h3 className={kit.proposalTitle}>Brightway Market to Groceries</h3>
                  <dl className={kit.fields}>
                    <dt>Applies</dt>
                    <dd>New charges from Sep 14 on</dd>
                    <dt>Skips</dt>
                    <dd>Charges over $150 - I'll ask, since those are more likely household goods</dd>
                  </dl>
                </div>
              )}
              <div className={kit.row}>
                <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={undo}>
                  Undo
                </button>
                {scope !== 'one' && (
                  <button
                    type="button"
                    className={`${kit.btn} ${kit.btnGhost}`}
                    onClick={() => {
                      setShowRule((v) => !v);
                      setFeedback(
                        showRule
                          ? 'Rule hidden. It stays active until you undo it.'
                          : 'The rule is spelled out, including where the agent will still ask.'
                      );
                    }}
                  >
                    {showRule ? 'Hide the rule' : 'Review the rule'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.records} aria-label="Your records">
          <span className={kit.label}>YOUR RECORDS</span>
          <ul className={styles.recordList}>
            {records.map((r) => (
              <li key={r.label} className={styles.record}>
                <div className={styles.recordText}>
                  <span className={styles.recordLabel}>{r.label}</span>
                  <span className={styles.recordValue}>{r.changed ? r.after : r.before}</span>
                </div>
                {stage === 'done' && (
                  <span className={`${kit.chip} ${r.changed ? kit.chipGood : ''}`}>
                    {r.changed ? 'Changed' : 'Not changed'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
